import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useCountriesList } from '../../../hooks';
import { POSTAL_CODE_MIN_LENGTH } from '../../../shared/constants/registration-form.constants';
import { localeFormat } from '../../../shared/utils';
import { preCheckStoreSelector } from '../../../store';
import {
  ContactInformationFormValues,
  ControlConfig,
  CountriesMap,
  UseTranslation,
} from '../types';
import { getControlsConfig, getCountriesData, getStatesData } from '../utils';

export const useControlsConfig = (
  t: UseTranslation,
  rootPath: string,
): ControlConfig[] => {
  const { setValue, clearErrors, watch } =
    useFormContext<ContactInformationFormValues>();

  const pageConfig = usePageContext();
  const countriesList = useCountriesList(
    `?locale=${localeFormat(pageConfig?.locale)}`,
  );

  const selectedCountry = watch(`${rootPath}country`);
  const postalCode = watch(`${rootPath}postalCode`);

  const preCheckInfo = preCheckStoreSelector.use.preCheckInfo();
  const isPrecheckedData = useRef(false);
  const selectedCountryReference = useRef<string>(selectedCountry);

  const isPostalCodeFilled =
    Number(postalCode?.length) >= POSTAL_CODE_MIN_LENGTH;

  // transform countriesList to a map for easier access
  const countriesMap = useMemo(() => {
    return countriesList.reduce((accumulator, country) => {
      accumulator[country.code] = country;
      return accumulator;
    }, {} as CountriesMap);
  }, [countriesList]);

  const countriesData = useMemo(() => {
    return getCountriesData(pageConfig, countriesMap);
  }, [pageConfig, countriesMap]);

  const statesData = useMemo(() => {
    return getStatesData(countriesMap, selectedCountry);
  }, [selectedCountry, countriesMap]);

  const controlsConfig = useMemo(
    () =>
      getControlsConfig(
        pageConfig,
        countriesData,
        statesData,
        selectedCountry,
        isPostalCodeFilled,
        t,
        rootPath,
      ),
    [
      pageConfig,
      countriesData,
      statesData,
      selectedCountry,
      isPostalCodeFilled,
      t,
      rootPath,
    ],
  );

  useEffect(() => {
    // prevent state clear when prechecked data is loaded
    if (isPrecheckedData.current) {
      isPrecheckedData.current = false;
      return;
    }

    if (selectedCountryReference.current === selectedCountry) return;

    selectedCountryReference.current = selectedCountry;

    // clear state field when country changes
    setValue(`${rootPath}state`, '');
    clearErrors(`${rootPath}state`);
  }, [selectedCountry, setValue, clearErrors, rootPath]);

  useEffect(() => {
    if (preCheckInfo && !isPrecheckedData.current) {
      isPrecheckedData.current = true;
    }
  }, [preCheckInfo]);

  // Since we have fields which depend on other fields, we should clear value
  // of fields which are being hidden.
  useEffect(() => {
    for (const control of controlsConfig) {
      if (!control.visible) {
        setValue(control.name, ''); // Clear value for hidden control
        clearErrors(control.name); // Clear errors for hidden control
      }
    }
  }, [controlsConfig, setValue, clearErrors]);

  return controlsConfig;
};
